import { FC, useCallback, useState } from 'react';
import AccountMatchingSection from './AccountMatchingSection';
import TokenSharingSection from './TokenSharingSection';
import { Button, ButtonStyles, DefaultFooterComponent, Icons, Popup, showAlert } from '@silverfort/sf-components';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { AccountMatchingSettingsForm } from '../types';
import { useUserManagementSettingsContext } from '../context/UserManagementSettingsContext';
import { getInitialFormValues, getUpdateAccountMatchingPayload } from '../utils';
import { UserManagementActionTypes } from '../reducer/types';
import { useDownloadAccountMatchingCsv } from '../api/useDownloadAccountMatchingCsv';
import { SettingsFeatureLock } from '../../../components/LockedFeatures/SettingsFeatureLock';
import { ACCOUNT_MATCHING_FIELDS } from '../../../common/fields';

const AccountMatchingSettings: FC = () => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { saveSettings, reducerState, dispatch } = useUserManagementSettingsContext();

    const form = useForm<AccountMatchingSettingsForm>({
        defaultValues: getInitialFormValues(reducerState),
        mode: 'onBlur',
        shouldUnregister: false
    });

    const { isValid, isDirty } = form.formState;
    const isSaveDisabled = !isValid || !isDirty || loading;
    const isAccountMatchingEnabled = useWatch({ control: form.control, name: 'isAccountMatchingEnabled' });

    const { generateAndDownloadAccountMatchingFile, loading: isDownloadAccountMatchingLoading } =
        useDownloadAccountMatchingCsv({
            onSuccess: useCallback(
                () => showAlert('File was downloaded successfully', 'success', { title: 'Export successful' }),
                []
            ),
            onError: useCallback((errorMessage) => {
                showAlert(errorMessage, 'error', {
                    title: 'Failed to export CSV'
                });
            }, [])
        });

    const handleSaveConfirmed = async (formValues: AccountMatchingSettingsForm) => {
        dispatch({
            type: UserManagementActionTypes.UPDATE_ACCOUNT_MATCHING_SETTINGS,
            payload: getUpdateAccountMatchingPayload(formValues)
        });

        try {
            setLoading(true);
            await saveSettings();
            form.reset(formValues);
            showAlert('Configuration will update in the next full AD sync', 'success', { title: 'Changes saved' });
        } catch (e: unknown) {
            showAlert('An error occurred while saving the changes', 'error', {
                title: 'Failed to save Changes'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = () => {
        setIsPopupOpen(true);
    };

    const handleCancel = () => {
        form.reset();
    };

    return (
        <FormProvider {...form}>
            <div className="h-full flex flex-col gap-4 py-4">
                <div className="flex flex-row justify-end">
                    <Button
                        buttonStyle={ButtonStyles.Tertiary}
                        text="Download CSV"
                        iconLeft={Icons.Download}
                        disabled={!isAccountMatchingEnabled || isDownloadAccountMatchingLoading}
                        onClick={generateAndDownloadAccountMatchingFile}
                    />
                </div>
                <div className="flex flex-col gap-4">
                    <AccountMatchingSection />
                    <SettingsFeatureLock id={ACCOUNT_MATCHING_FIELDS.IS_TOKEN_SHARING_ENABLED.ID}>
                        <TokenSharingSection />
                    </SettingsFeatureLock>
                </div>
                <div className="py-4 flex flex-row-reverse gap-2">
                    <Button
                        disabled={isSaveDisabled}
                        buttonStyle={ButtonStyles.PrimarySolid}
                        text="Save"
                        onClick={handleSave}
                    />
                    <Button buttonStyle={ButtonStyles.GhostSolid} text="Cancel" onClick={handleCancel} />
                </div>
            </div>
            <Popup
                open={isPopupOpen}
                popupClassName="w-[360px]"
                title="Save changes"
                ContentComponent={() => (
                    <span className="font-sf">
                        Clicking <span className="font-bold">Save</span> will make changes in your MFA User Management
                        table
                    </span>
                )}
                FooterComponent={DefaultFooterComponent}
                onClose={() => setIsPopupOpen(false)}
                onApprove={form.handleSubmit(handleSaveConfirmed)}
            />
        </FormProvider>
    );
};

export default AccountMatchingSettings;
